import { HTMLReactParserOptions } from 'html-react-parser'
import { FC, useMemo } from 'react'

import { Typography, TypographyProps } from '@mui/material'

import { HtmlContentParser } from '../HtmlContentParser/HtmlContentParser'
import { trimTooltipHtml } from './tooltipHtml'

interface TooltipHtmlContentProps extends Omit<TypographyProps<'div'>, 'children' | 'component'> {
  html?: string | null
  parserOptions?: HTMLReactParserOptions
}

const PARAGRAPH_GAP = 1

/**
 * Renders analyst-authored HTML inside a tooltip without stray vertical gaps.
 *
 * The HTML is cleaned with `trimTooltipHtml` and the default `<p>`, `<ul>` and
 * `<ol>` margins are reset, so the first and last block add no outer gap.
 * Consecutive blocks keep a small gap. The wrapper is a `div`, so a description that already
 * contains `<p>` is never nested inside another `<p>`.
 */
export const TooltipHtmlContent: FC<TooltipHtmlContentProps> = ({ html, parserOptions, sx, variant = 'body2', ...rest }) => {
  const cleanedHtml = useMemo(() => trimTooltipHtml(html), [html])

  if (!cleanedHtml) return null

  return (
    <Typography
      variant={variant}
      component="div"
      sx={[
        {
          '& p, & ul, & ol': { m: 0 },
          '& * + p, & * + ul, & * + ol': { mt: PARAGRAPH_GAP },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      <HtmlContentParser rawHtml={cleanedHtml} parserOptions={parserOptions} />
    </Typography>
  )
}
